import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import { Tooltip } from 'chaoskit/src/components';
import { formatNumber } from 'chaoskit/src/helpers/utility';

import BaseLayout from '../layouts/BaseLayout';
import Hero from '../partials/Hero';
import HeroImage from '../assets/media/downtime-hero.svg';
import { Data } from '../components';
import SocialGroup from '../partials/global/SocialGroup';
import { companies } from '../helpers/downtime-companies';

const AnimatedCard = ({ position, animation, digit }) => (
  <div className={`downtime__clock-flipCard ${position} ${animation}`}>
    <span>{digit}</span>
  </div>
);

AnimatedCard.propTypes = {
  position: PropTypes.string.isRequired,
  animation: PropTypes.string.isRequired,
  digit: PropTypes.any.isRequired,
};

// function component
const StaticCard = ({ position, digit }) => (
  <div className={position}>
    <span>{digit}</span>
  </div>
);

StaticCard.propTypes = {
  position: PropTypes.string.isRequired,
  digit: PropTypes.any.isRequired,
};

const FlipUnitContainer = ({ digit, shuffle, unit }) => {
  // assign digit values
  let currentDigit = digit;
  let previousDigit = digit - 1;

  // To prevent a negative value
  previousDigit = previousDigit === -1 ? 59 : previousDigit;

  // Add zero
  if (currentDigit < 10) {
    currentDigit = `0${currentDigit}`;
  }
  if (previousDigit < 10) {
    previousDigit = `0${previousDigit}`;
  }

  // Shuffle digits
  const digit1 = shuffle ? previousDigit : currentDigit;
  const digit2 = !shuffle ? previousDigit : currentDigit;

  // Shuffle animations
  const animation1 = shuffle ? 'is-folding' : 'is-unfolding';
  const animation2 = !shuffle ? 'is-folding' : 'is-unfolding';

  return (
    <div className="downtime__clock-unit-container">
      <div className="downtime__clock-unit">
        <StaticCard position="downtime__clock-upperCard" digit={currentDigit} />
        <StaticCard
          position="downtime__clock-lowerCard"
          digit={previousDigit}
        />
        <AnimatedCard position="first" digit={digit1} animation={animation1} />
        <AnimatedCard position="second" digit={digit2} animation={animation2} />
      </div>
      <h5 className="u-mt--regular u-textCenter">{unit}</h5>
    </div>
  );
};

FlipUnitContainer.propTypes = {
  digit: PropTypes.any.isRequired,
  shuffle: PropTypes.bool.isRequired,
  unit: PropTypes.string.isRequired,
};

const DownTimeSocial = props => {
  const { className, url } = props;

  const classes = cx('downtime__social', className);
  const text =
    'The Cost of Downtime for the Top US Ecommerce Sites from @gremlininc';

  return (
    <SocialGroup
      className={classes}
      networks={[
        {
          service: 'twitter',
          url: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
          title: 'Share on Twitter',
        },
        {
          service: 'linkedin',
          url: `https://www.linkedin.com/shareArticle?title=${text}&url=${url}`,
          title: 'Share on LinkedIn',
        },
        {
          service: 'facebook',
          url: `https://www.facebook.com/sharer.php?u=${url}`,
          title: 'Share on Facebook',
        },
      ]}
    />
  );
};

DownTimeSocial.propTypes = {
  className: PropTypes.string,
  url: PropTypes.string.isRequired,
};

class Downtime extends React.Component {
  state = {
    time: 0,
    minutes: 0,
    minutesShuffle: true,
    seconds: 0,
    secondsShuffle: true,
  };

  componentDidMount() {
    // Timer
    this.timerInterval = setInterval(() => this.adjustClock(), 1000);
  }

  componentDidUpdate() {
    const { time } = this.state;

    // Stop interval at 30 minutes
    if (time >= 1800) {
      clearInterval(this.timerInterval);
    }
  }

  componentWillUnmount() {
    clearInterval(this.timerInterval);
  }

  calculateScale = number => {
    // Pixel height of graph
    const height = 600;
    // Highest amount graph can handle
    const fullValue = 4000;
    // Figure out what each $ is equal to in px
    const scale = height / fullValue;

    return scale * number;
  };

  adjustClock() {
    const {
      time,
      minutes,
      minutesShuffle,
      seconds,
      secondsShuffle,
    } = this.state;

    const nextTime = time + 1;
    const nextState = {
      time: nextTime,
    };

    // Set time units
    const minutesCalculated = new Date(nextTime * 1000).getMinutes();
    const secondsCalculated = new Date(nextTime * 1000).getSeconds();

    // On minute chanage, update minutes and shuffle state
    if (minutesCalculated !== minutes) {
      nextState.minutes = minutesCalculated;
      nextState.minutesShuffle = !minutesShuffle;
    }

    // On second chanage, update seconds and shuffle state
    if (secondsCalculated !== seconds) {
      nextState.seconds = secondsCalculated;
      nextState.secondsShuffle = !secondsShuffle;
    }

    this.setState(nextState);
  }

  render() {
    const {
      data: {
        config: {
          siteMetadata: { siteUrl },
        },
        pageData: { childDataYaml: pageData },
      },
    } = this.props;

    const {
      time,
      minutes,
      minutesShuffle,
      seconds,
      secondsShuffle,
    } = this.state;

    // Workaround for Gatsby's location prop not reliably being available on server-loads
    const currentPageUrl = `${siteUrl}/ecommerce-cost-of-downtime/`;
    const downtimeTableCellHeaderClasses =
      'u-textXsmall u-textMuted u-textUppercase u-textHeading';

    return (
      <BaseLayout>
        <Helmet
          title={pageData.title}
          meta={[
            { name: 'description', content: pageData.description },
            {
              property: 'og:image',
              content: pageData.socialMediaImage,
            },
          ]}
        />
        <Hero contrast={false} particles={false} className="downtime__hero">
          <div className="hero-content">
            <img src={HeroImage} className="downtime__hero-image" alt="" />
            <div className="container">
              <div className="row row--largeGutter">
                <div className="column-6@medium u-contrast">
                  <h1 className="hero-title u-textFluid--h2-h3">
                    <Data>{pageData.hero.heading}</Data>
                  </h1>
                  <div className="hero-titleSub">
                    <Data markdown>{pageData.hero.blurb}</Data>
                  </div>
                  <div className="downtime__social-containerTop">
                    <DownTimeSocial url={currentPageUrl} />
                  </div>
                </div>
              </div>
              <div className="downtime__clock-wrapper u-contrast">
                <h5 className="u-textMuted u-textCenter u-textLeft@medium">
                  Session Timer
                </h5>
                <div className="downtime__clock">
                  <FlipUnitContainer
                    unit="Minutes"
                    digit={minutes}
                    shuffle={minutesShuffle}
                  />
                  <FlipUnitContainer
                    unit="Seconds"
                    digit={seconds}
                    shuffle={secondsShuffle}
                  />
                </div>
              </div>
              <aside className="downtime__table">
                <div className="downtime__table-row">
                  <div className="downtime__table-cell">
                    <div className={downtimeTableCellHeaderClasses}>
                      Company
                    </div>
                  </div>
                  <div className="downtime__table-cell u-hiddenDown@small">
                    <div className={downtimeTableCellHeaderClasses}>
                      Estimated Annual Ecommerce Revenue
                    </div>
                  </div>
                  <div className="downtime__table-cell">
                    <div className={downtimeTableCellHeaderClasses}>
                      Revenue loss per hour
                    </div>
                  </div>
                  <div className="downtime__table-cell u-hiddenDown@small">
                    <div className={downtimeTableCellHeaderClasses}>
                      Revenue loss per minute
                    </div>
                  </div>
                  <div className="downtime__table-cell">
                    <div className={downtimeTableCellHeaderClasses}>
                      Revenue loss this session
                    </div>
                  </div>
                </div>
                {companies.map(company => (
                  <div className="downtime__table-row" key={company.name}>
                    <div className="downtime__table-cell u-textHeading u-textMediumWeight u-textMuted">
                      <div className="downtime__table-companyWrapper">
                        <img alt={company.name} src={company.image} />
                        <span className="u-textSmall">{company.name}</span>
                      </div>
                    </div>
                    <div className="downtime__table-cell  u-hiddenDown@small">
                      ${formatNumber(company.revenue.year)}
                    </div>
                    <div className="downtime__table-cell">
                      ${formatNumber(company.revenue.second * 3600)}
                    </div>
                    <div className="downtime__table-cell  u-hiddenDown@small">
                      ${formatNumber(company.revenue.second * 60)}
                    </div>
                    <div className="downtime__table-cell">
                      ${formatNumber(company.revenue.second * time)}
                    </div>
                  </div>
                ))}
              </aside>
              <div className="u-mt--large">
                <DownTimeSocial className="u-flexEnd" url={currentPageUrl} />
              </div>
            </div>
          </div>
        </Hero>
        <section className="section section--large has-anchorBump">
          <div className="container">
            <div className="row u-flexMiddle row--xlargeGutter">
              <div className="column-6@large downtime__content">
                <h3 className="u-textTeal u-mb--large">
                  <Data>{pageData.section1.heading}</Data>
                </h3>
                <Data markdown>{pageData.section1.blurb}</Data>
              </div>
              <div className="column-6@large">
                <h5 className="u-textUppercase u-textHDefault u-textFluid--large u-mb--xlarge">
                  Revenue Per Second
                </h5>
                <aside className="downtime__graph-wrapper">
                  <main className="downtime__graph">
                    <div className="downtime__graph-legend">
                      <div className="u-textHeading u-textBold u-textMuted">
                        <span>$4,000</span>
                      </div>
                      <div className="u-textHeading u-textBold u-textMuted">
                        <span>$3,000</span>
                      </div>
                      <div className="u-textHeading u-textBold u-textMuted">
                        <span>$2,000</span>
                      </div>
                      <div className="u-textHeading u-textBold u-textMuted">
                        <span>$1,000</span>
                      </div>
                      <div className="u-textHeading u-textBold u-textMuted">
                        <span>$0</span>
                      </div>
                    </div>
                    <div className="downtime__graph-column--amazon">
                      <Tooltip
                        placement="left"
                        content={
                          <div className="u-pa--xsmall">
                            <div className="u-textHeading u-textMediumWeight u-mb--xsmall">
                              {companies[0].name}
                            </div>
                            <span className="u-textXsmall">
                              ${companies[0].revenue.second}/sec
                            </span>
                          </div>
                        }
                      >
                        <div
                          className="downtime__graph-bar"
                          style={{
                            height: this.calculateScale(
                              companies[0].revenue.second
                            ),
                            background: companies[0].color,
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div className="downtime__graph-column--everyone">
                      {companies.map((company, index) => {
                        // Skip over Amazon
                        if (index === 0) return;

                        return (
                          <Tooltip
                            key={company.name}
                            placement="left"
                            content={
                              <div className="u-pa--xsmall">
                                <div className="u-textHeading u-textMediumWeight u-mb--xsmall">
                                  {company.name}
                                </div>
                                <span className="u-textXsmall">
                                  ${company.revenue.second}/sec
                                </span>
                              </div>
                            }
                          >
                            <div
                              className="downtime__graph-bar"
                              style={{
                                height: this.calculateScale(
                                  company.revenue.second
                                ),
                                background: company.color,
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  </main>
                  <footer className="downtime__graph-footer">
                    <div className="u-textHeading u-textSmall u-textMuted u-textBold">
                      Amazon.com
                    </div>
                    <div className="u-textHeading u-textSmall u-textMuted u-textBold">
                      Traditional Retailers
                    </div>
                  </footer>
                </aside>
              </div>
            </div>
            <div id="metrics" className="u-textCenter u-mt--xlarge u-textMuted">
              Data Source:{' '}
              <a
                className="u-textBold u-linkMuted u-textUnderline"
                target="_blank"
                rel="noopener noreferrer"
                href="https://retail-index.emarketer.com/dashboard/c/retailers/5374f2454d4afd2bb4446565/ttm/AllCompanies/All%20Sectors"
              >
                Emarketer retail index
              </a>
            </div>
          </div>
        </section>
      </BaseLayout>
    );
  }
}

Downtime.propTypes = {
  data: PropTypes.object.isRequired,
};

export default Downtime;

export const pageQuery = graphql`
  query DowntimePageData {
    pageData: file(name: { eq: "ecommerce-cost-of-downtime" }) {
      childDataYaml {
        title
        socialMediaImage
        description
        hero {
          heading
          blurb
        }
        section1 {
          heading
          blurb
        }
      }
    }

    config: site {
      siteMetadata {
        title
        description
        siteUrl
      }
    }
  }
`;
